import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const categoryCopy = {
  all: { title: "All Pleasures", tag: "The complete house catalogue." },
  lingerie: { title: "Lingerie", tag: "Silk, lace, and shadow." },
  toys: { title: "Pleasure Objects", tag: "Sculptural. Whisper-quiet." },
  accessories: { title: "Accessories", tag: "Leather, brass, ribbon." },
  wellness: { title: "Wellness", tag: "Oils, candles, rituals." },
  games: { title: "Games", tag: "Slow nights, long questions." },
  companions: { title: "AI Companions", tag: "Always awake. Entirely yours." },
  basement: { title: "The Basement", tag: "Discipline, ritual, restraint.", isRoom: true },
  rooftop: { title: "The Rooftop", tag: "Under the crescent — slow-burn nights.", isRoom: true },
};

// Curated rooms — not real categories, but themed keyword collections.
// The backend accepts ?room=<key> and applies a regex over name + tags.
const ROOM_SLUGS = new Set(["basement", "rooftop"]);

// Shopper-facing tag vocabulary — must stay in sync with backend product_tagger.TAG_VOCAB
const FILTER_TAGS = [
  "beginner-friendly", "luxury", "travel-size", "couples", "discreet",
  "gift-ready", "waterproof", "whisper-quiet", "rechargeable", "remote-control",
  "hands-free", "silicone", "leather", "vegan",
];

const Shop = () => {
  const { category = "all" } = useParams();
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState("featured");
  const [activeTags, setActiveTags] = useState([]);
  const meta = categoryCopy[category] || categoryCopy.all;

  useEffect(() => {
    let q;
    if (category === "all") q = {};
    else if (ROOM_SLUGS.has(category)) q = { params: { room: category } };
    else q = { params: { category } };
    api.get("/products", q).then((r) => setProducts(r.data || []));
    setActiveTags([]);  // reset tag picks when the room changes
  }, [category]);

  const toggleTag = (t) =>
    setActiveTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  // Only surface tag chips the current inventory actually uses (no empty filters)
  const availableTags = useMemo(() => {
    const inUse = new Set();
    products.forEach((p) => (p.tags || []).forEach((t) => inUse.add(t)));
    return FILTER_TAGS.filter((t) => inUse.has(t));
  }, [products]);

  const filtered = activeTags.length === 0
    ? products
    : products.filter((p) => {
        const pt = new Set(p.tags || []);
        // AND semantics — every picked tag must be present
        return activeTags.every((t) => pt.has(t));
      });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === "price-asc") return a.price - b.price;
    if (sort === "price-desc") return b.price - a.price;
    if (sort === "name") return a.name.localeCompare(b.name);
    return 0;
  });

  return (
    <div data-testid="shop-page" className="px-6 md:px-12 lg:px-16 py-16 md:py-24">
      <div className="mb-12">
        <div className="flex flex-wrap gap-3 mb-8 text-xs uppercase tracking-[0.22em] text-muted-bone">
          {Object.keys(categoryCopy).map((slug) => (
            <Link
              key={slug}
              data-testid={`shop-filter-${slug}`}
              to={slug === "all" ? "/shop" : `/shop/${slug}`}
              className={`px-4 py-2 border transition ${
                category === slug
                  ? "text-bone"
                  : "hover:text-bone"
              }`}
              style={{
                borderColor:
                  category === slug ? "var(--rs-terracotta)" : "var(--rs-border)",
              }}
            >
              {categoryCopy[slug].title}
            </Link>
          ))}
        </div>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="overline mb-3">The Collection</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl text-bone leading-none">
              {meta.title}
            </h1>
            <p className="text-muted-bone mt-3 italic">{meta.tag}</p>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-[0.22em] text-muted-bone">
              Sort
            </span>
            <select
              data-testid="shop-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-transparent border px-4 py-2 text-sm text-bone outline-none"
              style={{ borderColor: "var(--rs-border)" }}
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price · Low to high</option>
              <option value="price-desc">Price · High to low</option>
              <option value="name">Name · A–Z</option>
            </select>
          </div>
        </div>

        {availableTags.length > 0 && (
          <div
            data-testid="shop-tag-filter"
            className="mt-8 flex flex-wrap items-center gap-2"
          >
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-bone mr-2">
              Refine —
            </span>
            {availableTags.map((t) => {
              const active = activeTags.includes(t);
              return (
                <button
                  key={t}
                  data-testid={`shop-tag-chip-${t}`}
                  onClick={() => toggleTag(t)}
                  className="text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 transition"
                  style={{
                    color: active ? "var(--rs-bone, #f5f2eb)" : "var(--rs-terracotta, #c07050)",
                    background: active ? "var(--rs-oxblood)" : "transparent",
                    border: `1px solid ${active ? "var(--rs-oxblood)" : "rgba(192,112,80,0.35)"}`,
                  }}
                >
                  {t}
                </button>
              );
            })}
            {activeTags.length > 0 && (
              <button
                data-testid="shop-tag-clear"
                onClick={() => setActiveTags([])}
                className="text-[10px] uppercase tracking-[0.22em] text-muted-bone hover:text-bone ml-2 underline underline-offset-4"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {sorted.length === 0 ? (
        <div className="text-muted-bone py-24 text-center">
          Nothing here yet. Try another room.
        </div>
      ) : (
        <div
          data-testid="shop-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          {sorted.map((p) => (
            <ProductCard key={p.product_id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Shop;
